import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import { getCurrentUser } from '../../services/auth';
import { learningAPI } from '../../services/api';
import { isTenantScopedAdmin } from '../../utils/role';

const statusLabels = {
  no_feedback: 'No feedback',
  feedback_added: 'Feedback added',
  student_viewed: 'Student viewed',
  student_acknowledged: 'Student acknowledged',
  follow_up_requested: 'Follow-up requested',
  resolved: 'Resolved',
};

const statusColors = {
  no_feedback: 'default',
  feedback_added: 'info',
  student_viewed: 'primary',
  student_acknowledged: 'success',
  follow_up_requested: 'warning',
  resolved: 'success',
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function HistoryFeedbackPanel({ historyId }) {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isUniversityUser = currentUser?.accountType === 'university';
  const isStaff = isTenantScopedAdmin(currentUser);
  const [feedback, setFeedback] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(isUniversityUser);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadFeedback = useCallback(async () => {
    if (!isUniversityUser || !historyId) return;
    setLoading(true);
    try {
      const response = await learningAPI.getHistoryFeedback(historyId);
      setFeedback(response.data?.feedback || { status: 'no_feedback', comments: [] });
      setError('');
    } catch (requestError) {
      if (requestError.response?.status === 403 || requestError.response?.status === 404) {
        setFeedback(null);
      } else {
        setError(requestError.response?.data?.message || 'Unable to load tutor feedback.');
      }
    } finally {
      setLoading(false);
    }
  }, [historyId, isUniversityUser]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  if (!isUniversityUser) return null;

  const addComment = async () => {
    const body = comment.trim();
    if (!body) return;
    setSaving(true);
    try {
      const response = await learningAPI.addHistoryFeedbackComment(historyId, body);
      setFeedback(response.data?.feedback);
      setComment('');
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to save this comment.');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      const response = await learningAPI.updateHistoryFeedbackStatus(historyId, status);
      setFeedback(response.data?.feedback);
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to update the feedback status.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography variant="body2">Loading tutor feedback…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (!feedback) return null;

  const status = feedback.status || 'no_feedback';
  const hasFeedback = status !== 'no_feedback';
  const canComment = isStaff || hasFeedback;

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-labelledby="tutor-feedback-title"
      sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <ForumRoundedIcon color="primary" />
            <Typography id="tutor-feedback-title" variant="h6" sx={{ fontWeight: 900 }}>
              Tutor feedback
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isStaff
              ? 'Leave specific, actionable guidance and close the loop when it has been addressed.'
              : 'Read, acknowledge, or ask your tutor a focused follow-up question.'}
          </Typography>
        </Box>
        <Chip
          label={statusLabels[status] || status.replaceAll('_', ' ')}
          color={statusColors[status] || 'default'}
          variant={status === 'no_feedback' ? 'outlined' : 'filled'}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 800 }}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {Array.isArray(feedback.comments) && feedback.comments.length > 0 ? (
        <Stack spacing={1.25} sx={{ mt: 2.5 }}>
          {feedback.comments.map((entry) => {
            const ownComment = entry.author?.id === (currentUser?.id || currentUser?._id);
            return (
              <Box
                key={entry.id}
                sx={{
                  alignSelf: ownComment ? 'flex-end' : 'flex-start',
                  width: { xs: '100%', sm: 'min(88%, 720px)' },
                  borderRadius: 2.5,
                  bgcolor: ownComment ? 'rgba(25, 118, 210, 0.06)' : 'grey.50',
                  border: '1px solid',
                  borderColor: ownComment ? 'primary.light' : 'divider',
                  p: 1.75,
                }}
              >
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {entry.author?.name || 'University member'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(entry.createdAt)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                  {entry.body}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Alert severity="info" sx={{ mt: 2.5 }}>
          {isStaff
            ? 'No tutor feedback has been added. Start with one clear observation and a practical next step.'
            : 'Your tutor has not added feedback for this attempt.'}
        </Alert>
      )}

      {canComment && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            label={isStaff ? 'Add feedback or a follow-up comment' : 'Ask a follow-up question'}
            placeholder={isStaff
              ? 'Describe what was done well, what needs attention, and one useful next action.'
              : 'Be specific about what you would like clarified.'}
            inputProps={{ maxLength: 4000 }}
            helperText={`${comment.length}/4000`}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {isStaff && hasFeedback && status !== 'resolved' && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleOutlineRoundedIcon />}
                  onClick={() => updateStatus('resolved')}
                  disabled={saving}
                >
                  Mark resolved
                </Button>
              )}
              {isStaff && status === 'resolved' && (
                <Button variant="outlined" onClick={() => updateStatus('feedback_added')} disabled={saving}>
                  Reopen feedback
                </Button>
              )}
              {!isStaff && hasFeedback && ['feedback_added', 'student_viewed'].includes(status) && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => updateStatus('student_acknowledged')}
                  disabled={saving}
                >
                  Acknowledge
                </Button>
              )}
            </Stack>
            <Button
              variant="contained"
              onClick={addComment}
              disabled={saving || !comment.trim()}
            >
              {saving ? 'Saving…' : isStaff ? 'Add feedback' : 'Send follow-up'}
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  );
}

export default HistoryFeedbackPanel;
